"use client";

import React from "react";
import { useFormContext } from "react-hook-form";
import * as z from "zod";
import { Select, Switch } from "antd"; // adjust path if needed
import PreviousButtonIcon from "../icons/PreviousButtonIcon";
import { formSchema } from "./TripAuditInspection";
import axios from "axios";
import toast from "react-hot-toast";
import CustomSelect from "./../../../components/ui/CustomSelect";
import moment from "moment";
import CustomDatePicker from "./../../ui/shared/CustomDatePicker";
import CustomTimePicker from "./../../ui/shared/CustomTimePicker";
import ShowErrorMessage from "./../../ui/shared/ShowErrorMessage";
import { pricingPlans } from "../../../static_data/data";
import LoadingButtonAnimation from "../../ui/LoadingButtonAnimation";
import { useTripAudit } from "../../../contexts/TripAuditContext";
import { useEffect } from "react";
import { handleStart } from "../utils/helper";

type InvoiceFormData = z.infer<typeof formSchema>;

const InvoiceSetupForm = () => {
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useFormContext<InvoiceFormData>();

  const { setShowInvoicePreCheckout, setShowInvoiceForm } = useTripAudit();
  // Watch switch states (we manage them manually since they're not form inputs)
  const [isLoading, setIsLoading] = React.useState(false);
  const [primaryInvoice, setPrimaryInvoice] = React.useState(true);
  const [additionalCharges, setAdditionalCharges] = React.useState(true);
  const [milesLimit, setMilesLimit] = React.useState(true);
  const [lateReturnFee, setLateReturnFee] = React.useState(true);
  const [selectCurrency, setSelectCurrency] = React.useState("USD");
  const [fuelLabel, setFuelLabel] = React.useState("Full");
  // const [verifyLicense, setVerifyLicense] = React.useState(false);
  const [returnExpirationDate, setReturnExpirationDate] =
    React.useState<string>("");
  const [time, setTime] = React.useState<string>("");
  const formData = getValues();

  const inspectionPlan = watch("inspection.plan");
  const selectedPlan = pricingPlans.plans.find(
    (plan) => plan.value === inspectionPlan,
  );
  useEffect(() => {
    setValue("invoice.is_primary_invoice", primaryInvoice);
    setValue("invoice.is_trip_milage_limit", milesLimit);
    setValue("invoice.is_late_return_fee", lateReturnFee);
    setValue("invoice.is_additional_charges", additionalCharges);
    setValue("invoice.fuel_level", fuelLabel);
  }, [
    primaryInvoice,
    milesLimit,
    lateReturnFee,
    additionalCharges,
    selectCurrency,
    fuelLabel,
    setValue,
  ]);
  // also sync return_datetime whenever date/time changes
  useEffect(() => {
    if (lateReturnFee && returnExpirationDate && time) {
      const dateTimeStr = `${returnExpirationDate} ${time}`;
      const momentObj = moment(dateTimeStr, "YYYY-MM-DD HH:mm", true);
      if (momentObj.isValid()) {
        const iso = momentObj.utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSS+00:00");
        setValue("invoice.return_datetime", iso);
      } else {
        setValue("invoice.return_datetime", "");
      }
    } else {
      setValue("invoice.return_datetime", "");
    }
  }, [lateReturnFee, returnExpirationDate, time, setValue]);
  const planPrice = selectedPlan?.price ?? 0;
  const onFinalSubmit = async () => {
    let returnDatetimeISO = "";

    if (lateReturnFee && returnExpirationDate && time) {
      const dateTimeStr = `${returnExpirationDate} ${time}`;
      const momentObj = moment(dateTimeStr, "YYYY-MM-DD HH:mm", true);

      if (momentObj.isValid()) {
        returnDatetimeISO = momentObj
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSSSSS+00:00");
      } else {
        setIsLoading(false);
        toast.error("Invalid return date or time format");
        return;
      }
    }
    setValue("invoice.return_datetime", returnDatetimeISO);
    setValue("invoice.is_primary_invoice", primaryInvoice);
    setValue("invoice.is_trip_milage_limit", milesLimit);
    setValue("invoice.is_late_return_fee", lateReturnFee);
    setValue("invoice.is_additional_charges", additionalCharges);
    setShowInvoiceForm(false);
    setShowInvoicePreCheckout(true);
  };
  const durationDays = Number(formData?.inspection?.access_limit_time);
  const isValid = isWithinDuration(returnExpirationDate, durationDays);

  const scrollToTripwiseSection = () => {
    setTimeout(() => {
      document.getElementById("tripwise-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };
  return (
    <div className="min-h-full">
      <div className="bg-white">
        <div className="max-w-6xl mx-auto p-5">
          <button
            onClick={() => {
              setShowInvoiceForm(false);
              scrollToTripwiseSection();
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <PreviousButtonIcon />
            <span className="text-[14px] font-semibold text-[#999]">
              Previous
            </span>
          </button>

          <div className="max-w-[1200px] mx-auto w-full py-5">
            <div className="text-center mb-10">
              <h1 className="text-[20px] font-bold text-[#303030]">
                Invoice setup
              </h1>
              <p className="text-[#6F6464] text-[14px] leading-6 mt-2 ">
                Customize your pre-post drive cost. This will be attached and
                calculated in your generated stamp.
              </p>
            </div>

            {/* <form onSubmit={handleSubmit(onSubmit)} className="space-y-[10px]"> */}
            {/* Currency */}
            <div className="flex justify-end items-center gap-2.5 mb-2.5">
              <p>Invoice currency</p>
              <div>
                <Select
                  value={selectCurrency ? selectCurrency : "USD"}
                  onChange={(value) => {
                    setSelectCurrency(value as string);
                  }}
                  className="p-2.5 text-[#303030] w-full bg-[#F6F6F6] rounded-md focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
                  // className="w-full !border-none rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px]"
                  placeholder="Select currency"
                  options={[
                    { value: "USD", label: "USD ($)" },
                    { value: "EUR", label: "EUR (€)" },
                    { value: "GBP", label: "GBP (£)" },
                  ]}
                />
              </div>
            </div>

            <div className="space-y-2.5">
              {/* Primary Invoice Section */}
              <div className="bg-white border border-[#F6F6F6] rounded-md p-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={primaryInvoice}
                      onChange={setPrimaryInvoice}
                      className="bg-gray-300"
                      size="small"
                    />
                    <h2 className="text-[12px] font-semibold text-[#151515]">
                      Primary invoice
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Trip Charge */}
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Drive charge
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.trip_charge", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter amount"
                        disabled={!primaryInvoice}
                        className={`p-[10px] w-full bg-[#F6F6F6] text-[#303030] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!primaryInvoice && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        {selectCurrency || "USD"}
                      </span>
                    </div>
                    {errors.invoice?.trip_charge && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.trip_charge.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Security deposit
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.security_deposit", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter amount"
                        disabled={!primaryInvoice}
                        className={`p-[10px] w-full bg-[#F6F6F6] text-[#303030] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!primaryInvoice && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        {selectCurrency || "USD"}
                      </span>
                    </div>
                    {errors.invoice?.security_deposit && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.security_deposit.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* // Set trip millage limit */}
              <div className="bg-white border border-[#F6F6F6] rounded-md p-4">
                <div className="flex items-center gap-4 mb-4">
                  <Switch
                    checked={milesLimit}
                    onChange={setMilesLimit}
                    size="small"
                    className="bg-gray-300"
                  />
                  <h2 className="text-[12px] font-semibold text-[#151515]">
                    Set drive millage limit
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Drive mileage limit (km/mile)
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.trip_milage_limit", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter mileage"
                        disabled={!milesLimit}
                        className={`p-[10px] w-full bg-[#F6F6F6] text-[#303030] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!milesLimit && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        (km/mile)
                      </span>
                    </div>
                    {errors.invoice?.trip_milage_limit && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.trip_milage_limit.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Excess mileage fee (per km/mile)
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.excess_milage_fee", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter amount"
                        disabled={!milesLimit}
                        className={`p-2.5 w-full text-[#303030] bg-[#F6F6F6] rounded-md focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!milesLimit && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        {selectCurrency || "USD"}
                      </span>
                    </div>
                    {errors.invoice?.excess_milage_fee && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.excess_milage_fee.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Late return fee*/}
              <div className="bg-white border border-[#F6F6F6] rounded-md p-4">
                <div className="flex items-center gap-4 mb-4">
                  <Switch
                    checked={lateReturnFee}
                    onChange={setLateReturnFee}
                    size="small"
                    className="bg-gray-300"
                  />
                  <h2 className="text-[12px] font-semibold text-[#151515]">
                    Set late return fee
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Return time and date
                    </label>
                    <div className="flex items-center gap-2.5">
                      <div className="flex-1">
                        <CustomDatePicker
                          label=""
                          setDate={setReturnExpirationDate}
                          dateValue={returnExpirationDate}
                          color="bg-[#F6F6F6]"
                          disabled={!lateReturnFee}
                        />
                      </div>
                      <div className="">
                        <CustomTimePicker
                          setTime={setTime}
                          timeValue={time}
                          name="times"
                          label=""
                          color="bg-[#F6F6F6]"
                          disabled={!lateReturnFee}
                        />
                      </div>
                    </div>
                    {/* Show Zod required error */}
                    {errors.invoice?.return_datetime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.return_datetime.message}
                      </p>
                    )}

                    {/* Show duration validation error */}
                    {lateReturnFee && returnExpirationDate && !isValid && (
                      <ShowErrorMessage
                        errorMessage={`Return date and time must be within ${durationDays} days from today`}
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Late return fee (per hour)
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.late_return_fee", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter amount"
                        disabled={!lateReturnFee}
                        className={`p-[10px] w-full text-[#303030] bg-[#F6F6F6] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!lateReturnFee && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        {selectCurrency || "USD"}
                      </span>
                    </div>
                    {errors.invoice?.late_return_fee && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.late_return_fee.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Additional charges (while return)*/}
              <div className="bg-white border border-[#F6F6F6] rounded-md p-4">
                <div className="flex items-center gap-4 mb-4">
                  <Switch
                    checked={additionalCharges}
                    onChange={setAdditionalCharges}
                    size="small"
                    className="bg-gray-300"
                  />
                  <h2 className="text-[12px] font-semibold text-[#151515]">
                    Additional charges (while return)
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Standard cleaning charge
                    </label>
                    <div className="relative">
                      <input
                        {...register("invoice.standard_cleaning_charge", {
                          setValueAs: (v) => (v === "" ? undefined : Number(v)),
                        })}
                        placeholder="Enter amount"
                        disabled={!additionalCharges}
                        className={`p-[10px] text-[#303030] w-full bg-[#F6F6F6] rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] ${!additionalCharges && "cursor-not-allowed"}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] text-[12px]">
                        {selectCurrency || "USD"}
                      </span>
                    </div>
                    {errors.invoice?.standard_cleaning_charge && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoice.standard_cleaning_charge.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-[#6F6464] font-semibold text-[12px] mb-[5px] block">
                      Fuel/Battery level
                    </label>
                    <CustomSelect
                      options={[
                        "Full",
                        "1/2  Half",
                        "1/4 Quarter",
                        "3/4 Three-Quarters",
                      ]}
                      value={fuelLabel ? fuelLabel : "Full"}
                      onChange={(value) => setFuelLabel(value as string)}
                      placeholder="Select fuel label"
                      disabled={!additionalCharges}
                    />
                    {/* <Select
                      value={fuelLabel ? fuelLabel : "Full"}
                      onChange={(value) => {
                        setFuelLabel(value as string);
                      }}
                      style={{
                        backgroundColor: "#F00",
                      }}
                      className="w-full !border-none rounded-[6px] focus:outline-none placeholder:text-[#999] placeholder:text-[12px] "
                      options={[
                        { value: "Full", label: "Full" },
                        { value: "EUR", label: "1/2  Half" },
                        { value: "GBP", label: "1/4 Quarter" },
                        { value: "GBP", label: "3/4 Three-Quarters" },
                      ]}
                    /> */}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-12">
              <button
                onClick={handleSubmit(onFinalSubmit)}
                className="bg-[#2D65F2] text-white p-3 rounded-md mt-10 w-full text-[14px] font-semibold no-long-press"
              >
                {isLoading ? (
                  <LoadingButtonAnimation />
                ) : (
                  `Proceed to pay $${planPrice.toFixed(2)} ${selectCurrency || "USD"}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSetupForm;

function isWithinDuration(dateStr: string, durationDays: number): boolean {
  if (!dateStr) return false;

  const selected = moment(dateStr, "YYYY-MM-DD", true);
  if (!selected.isValid()) return false;

  const todayStart = moment().startOf("day");

  // Must be today or later
  if (selected.isBefore(todayStart)) {
    return false;
  }

  // Max = today + (durationDays - 1) full days
  // so for 7 days → can select up to day 6 → total 7 days possible
  const maxAllowed = todayStart.clone().add(durationDays - 1, "days");

  return selected.isSameOrBefore(maxAllowed);
}
